import { selectCurrentHotelId, selectCurrentHotelName } from 'accessors'
import Plausible from 'plausible-tracker'
import { useCallback } from 'react'
import { useSelector } from 'react-redux'

const domain = 'dashboard.directful.com'

const plausibleEvents = {
  SendNewEmail: 'SendNewEmail',
  SendNewChatMessage: 'SendNewChatMessage',
  LogOut: 'LogOut',
  SwitchHotel: 'SwitchHotel',
  ClickMobileAppDownloadButton: 'ClickMobileAppDownloadButton',
  UpdateNotificationConfiguration: 'UpdateNotificationConfiguration',
  UploadedOptedOutGuestList: 'UploadedOptedOutGuestList',
  UploadedOptedInGuestList: 'UploadedOptedInGuestList',
  UpdateUserPermissions: 'UpdateUserPermissions',
  EditUser: 'EditUser',
  CreateNewUser: 'CreateNewUser',
  ViewEditUserDialog: 'ViewEditUserDialog',
  ViewAddNewUserDialog: 'ViewAddNewUserDialog',
  UpdateTextBlast: 'UpdateTextBlast',
  CreateNewAudience: 'CreateNewAudience',
  CreateNewTextBlast: 'CreateNewTextBlast',
  UpdateAudience: 'UpdateAudience',
  StartTextBlast: 'StartTextBlast',
  ViewTextBlastResults: 'ViewTextBlastResults',
  ViewAnnouncementResults: 'ViewAnnouncementResults',
  CreateAnnouncement: 'CreateAnnouncement',
  ViewAnnouncementEditor: 'ViewAnnouncementEditor',
  UpdateCampaignMedia: 'UpdateCampaignMedia',
  UpdateDirectPromo: 'UpdateDirectPromo',
  UpdateOTAPromo: 'UpdateOTAPromo',
  UpdateCampaignContent: 'UpdateCampaignContent',
  ViewCampaignEditor: 'ViewCampaignEditor',
  DisableCampaign: 'DisableCampaign',
  EnableCampaign: 'EnableCampaign',
  StartChatFromArrivals: 'StartChatFromArrivals',
  StartChatFromReview: 'StartChatFromReview',
  ViewReviewsForSingleDate: 'ViewReviewsForSingleDate',
  ViewReservationDetail: 'ViewReservationDetail',
  ViewGuestProfile: 'ViewGuestProfile',
  ViewUpdateGuestDetailsDialog: 'ViewUpdateGuestDetailsDialog',
  ViewRegainedLostGuestSegment: 'ViewRegainedLostGuestSegment',
  UpdateChatbotResponses: 'UpdateChatbotResponses',
  NewDirectMessage: 'NewDirectMessage',
  UpdateGuestDetail: 'UpdateGuestDetail',
  ForceOptIn: 'ForceOptIn',
  InviteToOptIn: 'InviteToOptIn',
  OptOutGuest: 'OptOutGuest',
  ResolveChat: 'ResolveChat',
  AddArrivingGuest: 'AddArrivingGuest',
  SidebarNavModeActive: 'SidebarNavModeActive',
  SwitchToNewNavigation: 'SwitchToNewNavigation',
  SwitchToClassicNavigation: 'SwitchToClassicNavigation'
}

const testUserEmails = ['directfultest@gmail.com']
const productionDomain = 'dashboard.directful.com'

const usePlausible = () => {
  const subsidiaryId = useSelector(selectCurrentHotelId)

  const isInternalAdmin = useSelector(s => s.login.actuallyIsInternalAdmin)

  const userEmail = useSelector(s => s.login.currentUser.email)

  const isTestUser = testUserEmails.includes(userEmail)

  const isLoggedIn = useSelector(s => s.login.userLoggedIn)

  const isOnProduction = window.location.hostname === productionDomain

  const hotelName = useSelector(selectCurrentHotelName)

  let enableAutoPageviews
  let trackEvent

  if (isOnProduction && !isInternalAdmin && !isTestUser && isLoggedIn) {
    const plausible = Plausible({
      trackLocalhost: true,
      domain
    })

    enableAutoPageviews = plausible.enableAutoPageviews
    trackEvent = plausible.trackEvent
  }

  const init = useCallback(() => {
    if (
      isOnProduction &&
      !isInternalAdmin &&
      !isTestUser &&
      isLoggedIn &&
      enableAutoPageviews
    ) {
      enableAutoPageviews()
    }
  }, [isInternalAdmin, isLoggedIn, enableAutoPageviews, isTestUser, isOnProduction])

  const postEvent = useCallback(
    (eventType, props) => {
      if (isOnProduction && !isInternalAdmin && !isTestUser && isLoggedIn) {
        trackEvent(eventType, {
          props: {
            ...props,
            hotelName,
            subsidiaryId
          }
        })
      }
    },
    [
      isInternalAdmin,
      isLoggedIn,
      trackEvent,
      hotelName,
      subsidiaryId,
      isTestUser,
      isOnProduction
    ]
  )

  return { postEvent, init, plausibleEvents }
}

export default usePlausible
